import { useCallback } from "react";

import { useCoreContext } from "../context/global/CoreContext";
import { LANGUAGE_CONFIG, uiText } from "../config";
import { formatUiDate } from "../utils/datetime";

/**
 * Preferred-language copy and display-date helper for authenticated screens.
 * Reads `user.preferred_language` from CoreContext.
 *
 * @returns {{language: string, t: Function, formatUiDate: Function}} Language, copy, and dates.
 */
export function useUiText() {
  const { user } = useCoreContext();
  const language = user?.preferred_language || LANGUAGE_CONFIG.DEFAULT;

  /**
   * Resolve a bilingual copy entry for the current user language.
   *
   * @param {{en: string, es?: string}|null|undefined} copy Language-keyed strings.
   * @param {Object<string, string|number>} [replacements={}] `{name}` substitutions.
   * @returns {string} Localized string, falling back to English.
   */
  const t = useCallback(
    (copy, replacements) => uiText(copy, language, replacements),
    [language]
  );

  /**
   * Format a display date in the current user language.
   * English matches `formatDate`. Spanish localizes named month/weekday tokens only.
   *
   * @param {Date|string} date Date object or `YYYY-MM-DD` / ISO string.
   * @param {string} outputFormat Same tokens as `formatDate`.
   * @returns {string} Localized display date.
   */
  const formatDateForUi = useCallback(
    (date, outputFormat) => formatUiDate(date, outputFormat, language),
    [language]
  );

  return { language, t, formatUiDate: formatDateForUi };
}
